/**
 * ONB-2 — Role-driven personalization strings for the onboarding
 * wizard + guided tour.
 *
 * The rich role picker persists a `creative_role` on `profiles`
 * (photographer / videographer / drone_pilot / food_photographer /
 * vlogger / stop_motion / graphic_designer / other) alongside the
 * coarser `business_type` on organizations. This module maps that
 * role to a bundle of copy the surrounding UI can lean on:
 *
 *   • rich picker card copy (title + one-liner + scene key)
 *   • coarse `business_type` derived from the specific role
 *   • personalized tour "next action" for the final tour card
 *   • suggested first goal (Passport seed) + first project name
 *   • ActivationCard priority hints (v1 — hard-coded ordering)
 *
 * Every consumer imports from here so a copy or ordering tweak is
 * a one-file change. Flag for v1.5: replace the hard-coded map with
 * a proper personalization engine keyed on real usage.
 *
 * NCC-1 scene keys mirror the SwiftUI `CreatorScene` enum used by
 * `LearnView.swift` / `InfoCardDetail.swift`. Web still ships
 * gradient tiles (see `PALETTES` in src/pages/Learn.tsx) — a future
 * NCC port swaps those tiles for real cast scenes without touching
 * this file.
 */

import type { BusinessType } from '@/data/serviceTabPresets';

// ─────────────────────────────────────────────────────────────────
// Role catalog
// ─────────────────────────────────────────────────────────────────

/**
 * The 8 creative roles the picker offers. Values persist to
 * `profiles.creative_role` verbatim (except `other`, which persists
 * the studio's free-text answer instead of the literal 'other').
 */
export const CREATIVE_ROLES = [
  'photographer',
  'videographer',
  'drone_pilot',
  'food_photographer',
  'vlogger',
  'stop_motion',
  'graphic_designer',
  'other',
] as const;

export type CreativeRole = (typeof CREATIVE_ROLES)[number];

/**
 * One card in the rich role picker.
 *
 * `sceneKey` maps to `PALETTES[sceneKey]` in `src/pages/Learn.tsx`
 * so the picker tile can render the same gradient hero the Learn
 * cards use. `businessType` is what the wizard persists to
 * `organizations.business_type` — the coarse category driving
 * service-tab presets. `other` intentionally maps to null so the
 * wizard falls back to the "General Inquiries" default preset.
 */
export interface RoleCardCopy {
  role: CreativeRole;
  /** Big card title. */
  title: string;
  /** One-line description under the title. */
  tagline: string;
  /** Scene palette key — matches `PALETTES` in Learn.tsx. */
  sceneKey: string;
  /**
   * Coarse `business_type` this role maps to. Used to preset the
   * service-tab defaults. `null` for `other` (no preset applies).
   */
  businessType: BusinessType | null;
}

export const ROLE_CARDS: RoleCardCopy[] = [
  {
    role: 'photographer',
    title: 'Photographer',
    tagline: 'Weddings, portraits, editorial — all of it.',
    sceneKey: 'astrophotographer',
    businessType: 'photographer',
  },
  {
    role: 'videographer',
    title: 'Videographer',
    tagline: 'Films, brand cuts, wedding highlights.',
    sceneKey: 'documentarian',
    businessType: 'videographer',
  },
  {
    role: 'drone_pilot',
    title: 'Drone pilot',
    tagline: 'Aerials for real estate, resorts, and brands.',
    sceneKey: 'dronePilot',
    businessType: 'videographer',
  },
  {
    role: 'food_photographer',
    title: 'Food photographer',
    tagline: 'Menus, cookbooks, cravings-on-a-plate work.',
    sceneKey: 'foodPhotographer',
    businessType: 'photographer',
  },
  {
    role: 'vlogger',
    title: 'Vlogger / content creator',
    tagline: 'Weekly videos, brand deals, a real audience.',
    sceneKey: 'vlogger',
    businessType: 'videographer',
  },
  {
    role: 'stop_motion',
    title: 'Stop-motion animator',
    tagline: 'Frame-by-frame worlds for brands and film.',
    sceneKey: 'stopMotion',
    businessType: 'videographer',
  },
  {
    role: 'graphic_designer',
    title: 'Graphic designer',
    tagline: 'Identity systems, packaging, editorial layouts.',
    // NCC-5 — real designer scene now available on web
    // (`07-sol-designer`). iOS still resolves this key to the
    // stand-in until scenes 1–24 port to SwiftUI (NCC-6).
    sceneKey: 'designer',
    businessType: 'hybrid',
  },
  {
    role: 'other',
    title: 'Something else',
    tagline: 'Tell Nova what you make — she adapts.',
    sceneKey: 'astrophotographer',
    businessType: null,
  },
];

/** Fast lookup by role. */
const ROLE_INDEX: Record<CreativeRole, RoleCardCopy> = ROLE_CARDS.reduce(
  (acc, card) => {
    acc[card.role] = card;
    return acc;
  },
  {} as Record<CreativeRole, RoleCardCopy>,
);

export function getRoleCard(role: CreativeRole): RoleCardCopy {
  return ROLE_INDEX[role] ?? ROLE_INDEX.other;
}

// ─────────────────────────────────────────────────────────────────
// Persistence value for `profiles.creative_role`
// ─────────────────────────────────────────────────────────────────

/**
 * The value we actually write to `profiles.creative_role`. For every
 * role except `other`, that's the role slug itself. For `other`, we
 * write the studio's free-text answer verbatim (so a Cake designer
 * shows up as "Cake designer" rather than the literal "other").
 * Returns `null` if the free-text is blank — nothing meaningful to
 * persist yet.
 */
export function creativeRolePersistValue(
  role: CreativeRole | null,
  otherText: string,
): string | null {
  if (!role) return null;
  if (role === 'other') {
    const trimmed = otherText.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  return role;
}

// ─────────────────────────────────────────────────────────────────
// Role → personalized copy for the guided tour + first-goal seed
// ─────────────────────────────────────────────────────────────────

/**
 * A single suggestion chip surfaced on the Nova chat empty state.
 * The chip label is what the studio sees on the tile; `prompt` is
 * what lands in the composer when they tap it. Both stay short so
 * the empty state doesn't feel like a wall of copy.
 */
export interface NovaChip {
  label: string;
  prompt: string;
}

/** Personalized copy the tour + wizard both consume. */
export interface RolePersonalization {
  /**
   * The imperative line shown on the tour's final "Ready?" card.
   * Rendered inside the Fraunces title so keep it short + concrete.
   */
  tourFinaleAction: string;
  /** Body paragraph under the finale — 1–2 sentences. */
  tourFinaleBody: string;
  /** Suggested first Passport goal (seed on the Passport onboarding). */
  suggestedFirstGoal: string;
  /** Suggested first project name (seed on the project step). */
  suggestedFirstProject: string;
  /**
   * ActivationCard priority — the id (from ActivationCard) the studio
   * sees BEFORE the default ordering kicks in. Consumers fold this to
   * the top when the studio's `creative_role` matches.
   */
  activationPriorityId: string;
  /**
   * ONB-3 — Studio Sites template preselected on lazy-create. Must
   * match a `SiteTemplate` id in `src/services/sites.ts`. Only kicks
   * in on FRESH site creation; existing sites keep their template.
   */
  siteTemplate: string;
  /**
   * ONB-3 — Learn tab "Every Tab" card ordering override. Card ids
   * that should be surfaced first, in order. Any cards not listed
   * here render after in their default order. Card ids match the
   * `LearnCard.id` values in `src/pages/Learn.tsx`.
   */
  learnEveryTabOrder: string[];
  /**
   * ONB-3 — Nova chat empty-state suggestion chips (4 tiles). Wired
   * into the "prompt" branch of `BRIDGE_SUGGESTIONS` in
   * `src/pages/Assistant.tsx`; the two design tiles (page/contract)
   * stay generic across roles.
   */
  novaChatChips: NovaChip[];
  /**
   * ONB-3 — Campaign type pre-filled on the first project. Must be
   * a value in the `campaign_type` enum (`src/integrations/supabase/
   * types.ts` — product/brand/story/launch/event/documentary/
   * commercial/music_video/other). Only fires when the studio has
   * zero projects yet.
   */
  firstProjectCampaignType: string;
  /**
   * ONB-3 — Milestones seeded on the studio's first project. Sent
   * to `useAddMilestone` in order after project insert. Fresh
   * studios only — subsequent projects stay blank so the studio
   * can shape their own template.
   */
  firstProjectMilestones: string[];
  /**
   * ONB-3 — Short label used in the Learn tab role chip ("Learning
   * as a Photographer"). Falls back to the role card title when
   * unset. Keep it short and lowercase-friendly.
   */
  roleChipLabel: string;
}

// v1 mapping. Room to grow — add per-role finale body variants, more
// aggressive activation priority (e.g. multiple pinned ids) once real
// signup analytics show which roles bounce on which cards.
//
// ONB-3 additions: `siteTemplate` preselects a Studio Sites template
// on lazy-create; `learnEveryTabOrder` reorders the Learn tab middle
// section; `novaChatChips` swaps the four "prompt" tiles on the Nova
// empty state; `firstProjectCampaignType` + `firstProjectMilestones`
// pre-fill the studio's very first project. Every field falls back to
// safe defaults if the role is missing or unknown (see `getPersonalization`).
export const ROLE_PERSONALIZATION: Record<CreativeRole, RolePersonalization> = {
  photographer: {
    tourFinaleAction: 'Import your Pixieset clients.',
    tourFinaleBody:
      "Bring over the people you already work with and Orbit becomes useful on day one. Settings → Clients → Import.",
    suggestedFirstGoal: 'Book 5 shoots this month',
    suggestedFirstProject: 'Portrait sessions — Q2',
    activationPriorityId: 'watermark',
    // SITES-2 — the old six were retired from the picker, so a fresh
    // studio seeded onto one would land on a template they could never
    // find again. Every role below now points at a shipping template.
    siteTemplate: 'broadsheet',
    learnEveryTabOrder: ['galleries', 'clients', 'passport', 'money'],
    novaChatChips: [
      { label: 'Draft a delivery email', prompt: "Draft a delivery email for the Chen wedding — friendly, warm, gallery link in the middle." },
      { label: "How's Q2 booking pace?", prompt: "How's my Q2 booking pace looking against Q1? Give me a shape." },
      { label: 'Most-loved gallery', prompt: "Which recent gallery has the most client hearts? Show me the top three shots." },
      { label: 'Rebook past clients', prompt: "Which past clients haven't booked in over a year? Draft a warm rebook email." },
    ],
    firstProjectCampaignType: 'story',
    firstProjectMilestones: ['Consultation', 'Shoot day', 'Culling + edit', 'Gallery delivery', 'Print upsell'],
    roleChipLabel: 'Photographer',
  },
  videographer: {
    tourFinaleAction: 'Upload your first cut.',
    tourFinaleBody:
      "Send a client a proof link they can review and comment on in-line. Deliveries → New delivery → drop a file.",
    suggestedFirstGoal: 'Deliver 3 highlight films',
    suggestedFirstProject: 'Brand campaign — reel edit',
    activationPriorityId: 'mux',
    siteTemplate: 'reel',
    learnEveryTabOrder: ['galleries', 'money', 'projects', 'passport'],
    novaChatChips: [
      { label: 'Draft a cut approval', prompt: "Draft a client email about the highlight film cut — invite feedback, gate the download until they approve." },
      { label: 'Longest render', prompt: "Which video delivery is taking longest to render? Nudge me if I need to check on it." },
      { label: 'Monthly delivery volume', prompt: "Show me my monthly delivery volume for the last 6 months — trend up or down?" },
      { label: 'Draft a bid', prompt: "Help me draft a bid for a 60-second brand film — three-tier pricing, deliverables spelled out." },
    ],
    firstProjectCampaignType: 'brand',
    firstProjectMilestones: ['Pre-production', 'Shoot day', 'Editorial cut', 'Client review', 'Final delivery'],
    roleChipLabel: 'Videographer',
  },
  drone_pilot: {
    tourFinaleAction: 'Add your aerial packages.',
    tourFinaleBody:
      "Build a booking link per package (mapping, real estate, event) so leads pick the right one on their own. Settings → Booking links.",
    suggestedFirstGoal: 'Book 4 aerial jobs this month',
    suggestedFirstProject: 'Resort aerials — coastal',
    activationPriorityId: 'booking',
    siteTemplate: 'reel',
    learnEveryTabOrder: ['calendar', 'projects', 'galleries', 'money'],
    novaChatChips: [
      { label: 'Permit reminder', prompt: "Set a permit-check reminder for my next scheduled aerial shoot." },
      { label: 'Flight time per project', prompt: "What's my average flight time per project this quarter? Any outliers?" },
      { label: 'Weather this week', prompt: "What's the shoot-day weather looking like for anything on my calendar this week?" },
      { label: 'Real estate proposal', prompt: "Draft a proposal for a real estate agent — three aerial packages, turnaround times, hero shot deliverables." },
    ],
    firstProjectCampaignType: 'commercial',
    firstProjectMilestones: ['Site scout', 'Permit + airspace check', 'Flight day', 'Edit + color', 'Deliver'],
    roleChipLabel: 'Drone pilot',
  },
  food_photographer: {
    tourFinaleAction: 'Set up your gallery watermark.',
    tourFinaleBody:
      "Watermark client proofs until the invoice clears — the mark lifts the moment they pay. Settings → Watermark.",
    suggestedFirstGoal: 'Land 2 recurring restaurant clients',
    suggestedFirstProject: 'Seasonal menu — sample plates',
    activationPriorityId: 'watermark',
    siteTemplate: 'index',
    learnEveryTabOrder: ['galleries', 'clients', 'money', 'calendar'],
    novaChatChips: [
      { label: 'Seasonal menu email', prompt: "Draft an email to Verden about the seasonal menu shoot — remind them of the deposit and lead time." },
      { label: 'Repeat restaurants', prompt: "Show me my repeat restaurant clients — anyone I should be checking in with this quarter?" },
      { label: 'Menu shoot proposal', prompt: "Draft a menu photography proposal — per-dish pricing, delivery format, usage rights." },
      { label: 'Dish count this year', prompt: "How many individual dishes have I shot this year? Which restaurant tops the list?" },
    ],
    firstProjectCampaignType: 'product',
    firstProjectMilestones: ['Menu review', 'Shot list', 'Shoot day', 'Cull + retouch', 'Deliver + license'],
    roleChipLabel: 'Food photographer',
  },
  vlogger: {
    tourFinaleAction: 'Set your first brand-deal rate.',
    tourFinaleBody:
      "Draft a rate card Nova can quote from when a brand DM lands. Settings → Pricing.",
    suggestedFirstGoal: 'Ship 4 videos this month',
    suggestedFirstProject: 'Content series — Q2 launch',
    activationPriorityId: 'mux',
    siteTemplate: 'reel',
    learnEveryTabOrder: ['galleries', 'money', 'clients', 'passport'],
    novaChatChips: [
      { label: 'Most-watched delivery', prompt: "What's my most-watched delivery in the last 90 days? What did it earn me?" },
      { label: 'Draft a sponsor pitch', prompt: "Draft a sponsor pitch email — cold, warm intro, my last three campaigns as social proof." },
      { label: 'Brand-deal rate check', prompt: "What should I quote for a 60-second dedicated brand video with usage rights? Compare against my last three deals." },
      { label: 'This month upload cadence', prompt: "How am I tracking on my upload cadence this month? Am I on pace to hit four videos?" },
    ],
    firstProjectCampaignType: 'launch',
    firstProjectMilestones: ['Concept + script', 'Shoot day', 'Rough cut', 'Sponsor review', 'Publish + promote'],
    roleChipLabel: 'Content creator',
  },
  stop_motion: {
    tourFinaleAction: 'Draft your first project brief.',
    tourFinaleBody:
      "Stop-motion clients ask for the same things — shot list, timing, delivery format. Save a template once and send it forever. Projects → New → Brief.",
    suggestedFirstGoal: 'Deliver 2 stop-motion spots',
    suggestedFirstProject: 'Product spot — 15s loop',
    activationPriorityId: 'contract',
    siteTemplate: 'reel',
    learnEveryTabOrder: ['projects', 'galleries', 'money', 'clients'],
    novaChatChips: [
      { label: 'Frame count this month', prompt: "How many frames have I shot this month? Which project ate the most?" },
      { label: 'Draft a licensing email', prompt: "Draft a licensing email — one-year usage renewal, adjust for inflation vs. last year's deal." },
      { label: 'Studio time by project', prompt: "How many studio days did each of my projects this quarter take? What's the trend on setup vs. shoot ratio?" },
      { label: 'New client brief', prompt: "Draft a stop-motion project brief template — shot list, timing per second, delivery formats, revision policy." },
    ],
    firstProjectCampaignType: 'commercial',
    firstProjectMilestones: ['Concept + storyboard', 'Set + rig build', 'Frame capture', 'Post + comp', 'Deliver + license'],
    roleChipLabel: 'Stop-motion animator',
  },
  graphic_designer: {
    tourFinaleAction: 'Send your first proposal.',
    tourFinaleBody:
      "Designer engagements start on paper. Draft your first proposal in Orbit and Stripe collects the deposit the moment they sign. Documents → New proposal.",
    suggestedFirstGoal: 'Sign 3 brand identity projects',
    suggestedFirstProject: 'Brand identity — new client',
    activationPriorityId: 'contract',
    siteTemplate: 'index',
    learnEveryTabOrder: ['projects', 'money', 'clients', 'calendar'],
    novaChatChips: [
      { label: 'Draft a project proposal', prompt: "Draft a brand identity proposal — three tiers (essentials, full system, ongoing retainer), deliverables per tier." },
      { label: 'Top revenue clients', prompt: "Show my top revenue clients this year — anyone I should be pitching a retainer to?" },
      { label: 'Draft a rate bump email', prompt: "Draft a rate-bump email to an existing retainer client — kind, firm, effective next billing cycle." },
      { label: 'Round-count per project', prompt: "What's my average revision count per identity project? Which project ran the longest?" },
    ],
    firstProjectCampaignType: 'brand',
    firstProjectMilestones: ['Discovery', 'Concept round', 'Refinement', 'Final files', 'Handoff + license'],
    roleChipLabel: 'Graphic designer',
  },
  other: {
    tourFinaleAction: 'Add your first client.',
    tourFinaleBody:
      "Every corner of Orbit gets useful the moment there's a real person in it. Clients → New client → then attach a project.",
    suggestedFirstGoal: 'Book my first paying gig',
    suggestedFirstProject: 'First client — kickoff',
    activationPriorityId: 'client',
    siteTemplate: 'broadsheet',
    learnEveryTabOrder: ['today', 'nova', 'projects', 'clients'],
    novaChatChips: [
      { label: 'Ask about my week', prompt: "What's on my plate this week? Give me a shape." },
      { label: 'Draft a client email', prompt: "Draft a friendly follow-up email to a client I haven't spoken to in a while." },
      { label: 'Show my top clients', prompt: "Who are my top-earning clients this quarter?" },
      { label: 'Log an expense', prompt: "Log a business expense — walk me through it." },
    ],
    firstProjectCampaignType: 'other',
    firstProjectMilestones: ['Kickoff', 'Production', 'Review', 'Delivery'],
    roleChipLabel: 'Creative',
  },
};

/** Convenience — resolve the personalization bundle, defaulting to `other`. */
export function getPersonalization(
  role: CreativeRole | null | undefined,
): RolePersonalization {
  if (!role) return ROLE_PERSONALIZATION.other;
  return ROLE_PERSONALIZATION[role] ?? ROLE_PERSONALIZATION.other;
}

/**
 * Read the persisted creative_role back off `profiles.creative_role`
 * and coerce it into one of our CreativeRole slugs. Anything unknown
 * (like a free-text `other` answer) resolves to `other`, which is
 * the safest personalization default.
 */
export function coerceCreativeRole(value: string | null | undefined): CreativeRole {
  if (!value) return 'other';
  const match = CREATIVE_ROLES.find((r) => r === value);
  return match ?? 'other';
}
